import { Box, Button, ButtonGroup } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import PageContainer from '../../../components/PageContainer';
import { useSnackbar } from 'notistack';
import CaseTable from '../../../components/CaseTable';
import { getMyNotRespondedPendingBankCases, getMyPendingBankCases, getMyRespondedPendingBankCases } from '../../../redux/api/cases';
import { getPrivilegeStatus } from '../../../util';

const sections = [
    {
        name: 'All Pending Cases',
        id: 'getMyPendingBankCases',
    },
    { name: 'Responded Cases', id: 'getMyRespondedPendingBankCases' },
    { name: 'Not Responded Cases', id: 'getMyNotRespondedPendingBankCases' },
];

const PendingBankCases = () => {
    const [processing, setProcessing] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [section, setSection] = useState(0);
    const [records, setRecords] = useState([]);
    const [respondedRecords, setRespondedRecords] = useState([]);
    const [notRespondedRecords, setNotRespondedRecords] = useState([]);
    const [paginationData, setPaginationData] = useState({});
    const [respondedPaginationData, setRespondedPaginationData] = useState({});
    const [notRespondedPaginationData, setNotRespondedPaginationData] = useState({});
    const [relationshipOfficers, setRelationshipOfficers] = useState([]);

    const init = async (start = 0, length = 50) => {
        try {
            setProcessing(true);
            const resp = await getMyPendingBankCases({ start, length });

            const res = resp?.resultObject?.dataTableResponse || {};
            setRecords(records?.length < start ? res?.resultObject?.data || [] : records.concat(res?.resultObject?.data || []));
            setRelationshipOfficers(resp?.resultObject?.relationshipOfficers || []);
            setPaginationData({
                start,
                length,
                total: res?.resultObject?.recordsTotal,
                totalFiltered: res?.resultObject?.recordsFiltered,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const fetchResponded = async (start = 0, length = 50) => {
        try {
            setProcessing(true);
            const res = await getMyRespondedPendingBankCases({ start, length });

            setRespondedRecords(respondedRecords?.length < start ? res?.resultObject?.data || [] : respondedRecords.concat(res?.resultObject?.data || []));

            setRespondedPaginationData({
                start,
                length,
                total: res?.resultObject?.recordsTotal,
                totalFiltered: res?.resultObject?.recordsFiltered,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const fetchNotResponded = async (start = 0, length = 50) => {
        try {
            setProcessing(true);
            const res = await getMyNotRespondedPendingBankCases({
                start,
                length,
            });
            setNotRespondedRecords(notRespondedRecords?.length < start ? res?.resultObject?.data || [] : notRespondedRecords.concat(res?.resultObject?.data || []));

            setNotRespondedPaginationData({
                start,
                length,
                total: res?.resultObject?.recordsTotal,
                totalFiltered: res?.resultObject?.recordsFiltered,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const search = async (values, closeSearchModal) => {
        try {
            const res = await getMyPendingBankCases(values);
            if (res?.statusCode == '000') {
                setRecords(res?.resultObject?.data || []);
                closeSearchModal();
            } else {
                enqueueSnackbar(res?.message || 'An error occurred', {
                    variant: 'info',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const exportData = async () => {
        try {
            const resp = await getMyPendingBankCases({}, true);
            enqueueSnackbar(resp?.message || 'Your report is being processed, kindly check the reports page for progress');
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const getRecords = (section) => {
        switch (section) {
            case 0:
                return records;
            case 1:
                return respondedRecords;
            case 2:
                return notRespondedRecords;
            default:
                return [];
        }
    };

    useEffect(() => {
        init();
        fetchResponded();
        fetchNotResponded();
    }, []);

    return (
        <PageContainer pageTitle="My Pending Bank Cases" processing={processing}>
            <ButtonGroup variant="outlined" className="pl-2 pr-2 mb-2" fullWidth>
                {sections.map(
                    (sec, key) =>
                        getPrivilegeStatus(sec.id) && (
                            <Button variant={section === key ? 'contained' : 'outlined'} key={key} onClick={() => setSection(key)}>
                                {sec.name}
                            </Button>
                        )
                )}
            </ButtonGroup>

            <CaseTable
                cases={getRecords(section)}
                initFunction={init}
                exportFunction={exportData}
                searchFunction={search}
                relationshipOfficers={relationshipOfficers}
                paginationData={section == 0 ? paginationData : section == 1 ? respondedPaginationData : section == 2 ? notRespondedPaginationData : {}}
            />
        </PageContainer>
    );
};

export default PendingBankCases;
